import { computed, type ComputedRef } from 'vue';
import { meSchema, type Me } from './me';
import type { UserFacade } from './user.facade';
import { DateTime } from 'luxon';
import type { Permission } from './permission';

export class UserService implements UserFacade {
  private me: Me;
  materialEditorMode = computed<'legacy' | 'new'>(() => {
    if (this.me.me.forceNewEditor) {
      return 'new';
    }

    const createdAt = DateTime.fromISO(this.me.me.createdAt);
    const legacyMaterialEditorCutoffDate = DateTime.fromISO(
      '2025-12-19T00:00:00.000Z',
    );
    const isB2Cuser = this.me.currentLicense.holderType === 'USER';
    const isCreatedAfterCutoff = createdAt > legacyMaterialEditorCutoffDate;
    return isB2Cuser && isCreatedAfterCutoff ? 'new' : 'legacy';
  });

  constructor(me: unknown) {
    const parsed = meSchema.safeParse(me);
    if (parsed.success) {
      this.me = parsed.data;
    } else {
      // NOTE: usually this should just throw and crash the entire application
      // however since there have been issues in the past where me was not correctly set for a while, lets use a fallback for now and remove it if the error below does not get thrown for the next couple of weeks
      console.error(
        `CRITICAL: Failed to parse me result: ${JSON.stringify(parsed.error)}`,
      );
      this.me = {
        externalIntegrations: [],
        me: {
          createdAt: '1970-01-01T13:42:35.516Z',
          forceNewEditor: false,
        },
        currentLicense: {
          holderType: 'USER',
          plan: {
            features: [],
          },
        },
      };
    }
  }

  hasPermission(permission: Permission): ComputedRef<boolean> {
    return computed(() => {
      return this.me.currentLicense.plan.features.some(
        (feature) => feature.key === permission,
      );
    });
  }

  hasIntegration(integrationKey: string): ComputedRef<boolean> {
    return computed(() => {
      return this.me.externalIntegrations.some(
        (integration) =>
          integration.externalIntegrationSetting.key === integrationKey,
      );
    });
  }

  _externalIntegrations = computed(() => {
    return this.me.externalIntegrations.map((integration) => {
      return {
        id: integration.id,
        userName: integration.displayName,
        integrationName: integration.externalIntegrationSetting.displayName,
        integrationKey: integration.externalIntegrationSetting.key,
        features: integration.externalIntegrationSetting.integrationFeatures,
      };
    });
  });

  externalIntegrations() {
    return this._externalIntegrations;
  }
}
